// src/hooks/useAccessControl.js
import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { ACTIONS, RESOURCES } from './usePermissions';

export const useAccessControl = () => {
  const { user, loading, hasPermission, refreshPermissions } = useAuth();

  const can = useCallback((resource, action = ACTIONS.READ) => {
    if (!resource) return true;
    return hasPermission(resource, action);
  }, [hasPermission]);

  const canAccess = useCallback((permission) => {
    if (!permission) return true;
    return can(permission.resource, permission.action || ACTIONS.READ);
  }, [can]);

  const canAny = useCallback((permissions = []) => {
    if (!Array.isArray(permissions) || permissions.length === 0) return true;
    return permissions.some((permission) => canAccess(permission));
  }, [canAccess]);

  const canAll = useCallback((permissions = []) => {
    if (!Array.isArray(permissions) || permissions.length === 0) return true;
    return permissions.every((permission) => canAccess(permission));
  }, [canAccess]);

  const isItemVisible = useCallback((item) => {
    if (item.access) {
      const canPassAll = item.access.all ? canAll(item.access.all) : true;
      const canPassAny = item.access.any ? canAny(item.access.any) : true;
      return canPassAll && canPassAny;
    }
    if (Array.isArray(item.permissions) && item.permissions.length > 0) {
      return item.requireAll === false ? canAny(item.permissions) : canAll(item.permissions);
    }
    return canAccess(item.permission);
  }, [canAccess, canAll, canAny]);

  const filterAuthorizedItems = useCallback((items = []) => {
    const filterRecursive = (nodes) =>
      nodes
        .map((node) => {
          if (!isItemVisible(node)) return null;
          if (!node.children) return node;
          const children = filterRecursive(node.children);
          return children.length === 0 ? null : { ...node, children };
        })
        .filter(Boolean);
    return filterRecursive(items);
  }, [isItemVisible]);

  return {
    user,
    loading,
    isAuthenticated: Boolean(user),
    can,
    canAccess,
    canAny,
    canAll,
    isItemVisible,
    cannot: (resource, action = ACTIONS.READ) => !can(resource, action),
    filterAuthorizedItems,
    refreshPermissions,
    RESOURCES,
    ACTIONS,
  };
};

export { RESOURCES, ACTIONS };